import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Clapperboard, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Re-check every 60s in case another admin runs Demo Mode in a different tab
const POLL_MS = 60_000;

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * Tiny status pill in the top nav (admins only) that shows whether the
 * showcase demo account is fully seeded + has an AI plan ready.
 *
 *   Green  = ready for live presentation
 *   Amber  = stale / missing data — click to open Demo Mode
 *   Red    = error fetching status
 *   Gray   = still loading
 *
 * Clicking navigates to /admin?tab=demo so the Demo Mode tab is preselected.
 */
function DemoHealthBadge() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/demo-mode/status`, authHeaders());
      setStatus(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch demo status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // Determine state
  let state;
  let label;
  let Icon = Clapperboard;
  let pillClasses = "";
  let dotClasses = "";

  if (loading) {
    state = "loading";
    label = "Demo: …";
    Icon = Loader2;
    pillClasses = "bg-white/10 text-white/80 hover:bg-white/15";
    dotClasses = "bg-white/40";
  } else if (error) {
    state = "error";
    label = "Demo: error";
    Icon = AlertTriangle;
    pillClasses = "bg-red-500/20 text-red-100 hover:bg-red-500/30";
    dotClasses = "bg-red-400";
  } else {
    const demo = status?.demo_user;
    const healthy =
      demo &&
      demo.lessons_completed >= 42 &&
      demo.session_assessments >= 7 &&
      demo.has_ai_plan;
    if (healthy) {
      state = "ready";
      label = "Demo: ready";
      Icon = CheckCircle2;
      pillClasses = "bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30";
      dotClasses = "bg-emerald-400";
    } else {
      state = "stale";
      label = "Demo: stale";
      Icon = AlertTriangle;
      pillClasses = "bg-amber-500/25 text-amber-100 hover:bg-amber-500/35";
      dotClasses = "bg-amber-300";
    }
  }

  // Tooltip detail
  const tooltip = (() => {
    if (loading) return "Checking demo account…";
    if (error) return error;
    const d = status?.demo_user;
    if (!d) return "Demo account not found";
    const parts = [
      `${d.lessons_completed}/42 lessons`,
      `${d.session_assessments}/7 reflections`,
      `${d.capstone_fields_count}/7 capstone fields`,
      d.has_ai_plan
        ? `AI plan v${d.ai_plan_version} (${d.ai_plan_remaining_regenerations} regens left)`
        : "AI plan not generated",
    ];
    return parts.join(" · ");
  })();

  return (
    <button
      onClick={() => navigate("/admin?tab=demo")}
      data-testid="demo-health-badge"
      data-state={state}
      title={tooltip}
      className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${pillClasses}`}
    >
      <span className="relative flex items-center">
        <span
          className={`absolute inline-flex h-2 w-2 rounded-full ${dotClasses} ${
            state === "ready" ? "animate-ping opacity-75" : ""
          }`}
        />
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dotClasses}`} />
      </span>
      <Icon
        className={`w-3.5 h-3.5 ${state === "loading" ? "animate-spin" : ""}`}
      />
      <span>{label}</span>
    </button>
  );
}

export default DemoHealthBadge;
